import {
  Card,
  Stepper,
  Title,
  rem,
  Center,
  Loader,
} from '@mantine/core';

import {
  ResultAction,
  Stripe,
  StripeEmbeddedCheckoutLineItemsChangeEvent,
  StripeEmbeddedCheckoutShippingDetailsChangeEvent,
  loadStripe,
} from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

import { useTranslation } from 'react-i18next';

import ReCAPTCHA from 'react-google-recaptcha';
import { useCallback, useEffect, useRef, useState } from 'react';

import ReactGA from 'react-ga4';
import { useError } from '@/contexts/ErrorContext';
import { useEloApi, ApiError } from '@/api/api';
import classes from './saveBookingPage.module.css';
import { AgendaConfiguration, IndexListResponse } from '@/api/agendaConfiguration';
import { Booking, CreateBooking } from '@/api/booking';
import { SaveBookingDate } from '@/components/SaveBookingDate/SaveBookingDate';
import { SaveBookingContactInfo } from '@/components/SaveBookingContactInfo/SaveBookingContactInfo';
import { User } from '@/api/users';
import { SaveBookingEnd } from '@/components/SaveBookingEnd/SaveBookingEnd';
import { SaveBookingChooseType } from '@/components/SaveBookingChooseType/SaveBookingChooseType';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * Embedded Checkout configuration options.
 * You can initially pass in `null` to `options.clientSecret` or
 * `options.fetchClientSecret` if you are performing an initial server-side
 * render or when generating a static site.
 */
export interface EmbeddedCheckoutProviderPropsOptions {
  clientSecret?: string | null;
  fetchClientSecret?: (() => Promise<string>) | null;
  onComplete?: () => void;
  onShippingDetailsChange?: (
    event: StripeEmbeddedCheckoutShippingDetailsChangeEvent
  ) => Promise<ResultAction>;
  onLineItemsChange?: (event: StripeEmbeddedCheckoutLineItemsChangeEvent) => Promise<ResultAction>;
}

/**
 * A [Stripe object](https://stripe.com/docs/js/initializing) or a `Promise`
 * resolving to a `Stripe` object.
 * The easiest way to initialize a `Stripe` object is with the the
 * [Stripe.js wrapper module](https://github.com/stripe/stripe-js/blob/master/README.md#readme).
 * Once this prop has been set, it can not be changed.
 *
 * You can also pass in `null` or a `Promise` resolving to `null` if you are
 * performing an initial server-side render or when generating a static site.
 */
// stripe: PromiseLike<stripeJs.Stripe | null> | stripeJs.Stripe | null;
/* */

interface SaveBookingPageProps {
  localUser: User;
  agendaConfiguration: AgendaConfiguration | null;
  listAgendaConfs: IndexListResponse[];
  setAgendaConfiguration: (agendaConfiguration: AgendaConfiguration) => void;
  extension: string;
}

export const SaveBookingPage = ({
  localUser,
  agendaConfiguration,
  listAgendaConfs,
  setAgendaConfiguration,
  extension,
}: SaveBookingPageProps) => {
  const hasSentPageView = useRef(false);
  useEffect(() => {
    if (!hasSentPageView.current) {
      ReactGA.send({
        hitType: 'pageview',
        page: '/save-booking',
      });
      hasSentPageView.current = true;
    }
  }, []);

  const api = useEloApi({});
  const errorContext = useError();

  const { t } = useTranslation();

  const [selectedAgId, setSelectedAgId] = useState<number>(agendaConfiguration?.id ?? 0);

  const [active, setActive] = useState(0);
  const nextStep = () => setActive((current) => (current < 5 ? current + 1 : current));
  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  const [receivedBooking, setReceivedBooking] = useState<Booking | null>(null);
  const [runPolling, setRunPolling] = useState(false);

  const [createBooking, setCreateBooking] = useState<CreateBooking>({
    agendaConfigurationId: agendaConfiguration?.id ?? 0,
    onSite: !!agendaConfiguration ? (agendaConfiguration.overwriteLocation ? agendaConfiguration.isOnSiteAllowed : localUser.isOnSiteAllowed) : false,
    name: '',
    phone: '',
    email: '',
    startDate: new Date(),
    patientId: null,
    comments: '',
  });
  const [loading, setLoading] = useState(false);

  const getSelectedConfiguration = useCallback(async () => {
    if (!selectedAgId) return;
    setLoading(true);
    try {
      const ac = await api.getAgendaConfiguration(selectedAgId, extension);
      setAgendaConfiguration(ac);
      setCreateBooking(current => {
        return {...current, agendaConfigurationId: selectedAgId, onSite: ac.overwriteLocation ? ac.isOnSiteAllowed : localUser.isOnSiteAllowed}
      })
    } catch (e) {
      console.error(e);
      if (e instanceof ApiError) {
        errorContext.setError(e.message);
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAgId, extension]);
  useEffect(() => {
    if (selectedAgId && agendaConfiguration?.id !== selectedAgId) {
      getSelectedConfiguration();
    }
  }, [agendaConfiguration?.id, selectedAgId]);

  const doCreateBooking = async () => {
    setLoading(true);
    try {
      if (!recaptchaRef.current) return;

      // Execute invisible reCAPTCHA
      const captchaToken = await recaptchaRef.current.executeAsync();
      const res = await api.createBooking(createBooking, '', extension, captchaToken);
      setReceivedBooking(res);
      ReactGA.event({
        category: 'Bookings',
        action: 'Patient Booking',
        label: 'Saved booking without payment',
      });
      nextStep();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // reCAPTCHA
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [stripe, setStripe] = useState<PromiseLike<Stripe | null> | null>(null);
  const [stripeOptions, setStripeOptions] = useState<EmbeddedCheckoutProviderPropsOptions>({
    onComplete: async () => {
      setRunPolling(true);
      nextStep();
      ReactGA.event({
        category: 'Bookings',
        action: 'Patient Booking',
        label: 'Saved booking with payment',
      });
    },
  });
  const doCreateBookingCheckout = async () => {
    setLoading(true);
    try {
      const res = await api.bookingCheckoutCreate(extension as string, createBooking);
      setStripeOptions({ ...stripeOptions, clientSecret: res.clientSecret });
      setStripe(loadStripe(res.stripePublishableKey));
      setReceivedBooking(res.booking);
      setLoading(false);
      nextStep();
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
        if (error.name === 'StripeAuthorizationError') {
          // Correo
          if (agendaConfiguration) setAgendaConfiguration({ ...agendaConfiguration, paymentOption: 0 });
          await doCreateBooking();
          return;
        }
      }
      errorContext.setError(errorMessage);
      console.error(error);
    }
  };

  const pollingFunc = async (stoppingState: boolean) => {
    if (stoppingState || !receivedBooking) return;
    try {
      const res = await api.getBooking(receivedBooking.id);
      setReceivedBooking(res);
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
        if (errorMessage === 'Booking no encontrado') {
          setReceivedBooking(null);
          return;
        }
      }
      errorContext.setError(errorMessage);
      console.error(error);
      setReceivedBooking(null);
    }
  };

  const pollingRef = useRef<NodeJS.Timeout | undefined>();
  useEffect(() => {
    const startPolling = (stoppingState: boolean) => {
      pollingRef.current = setInterval(() => {
        pollingFunc(stoppingState);
      }, 3000); // Poll every 1 second
    };
    if (runPolling) {
      startPolling(!!receivedBooking?.paid);
    }

    return () => {
      clearInterval(pollingRef.current);
    };
  }, [receivedBooking, runPolling]);

  const isLocationAvailableByAgendaConfiguration = !!agendaConfiguration && (agendaConfiguration.isOnSiteAllowed && agendaConfiguration.isVirtualAllowed);
  const isLocationAvailableByUser = localUser.isOnSiteAllowed && localUser.isVirtualAllowed;
  const isLocationAvailable = !!agendaConfiguration && ((!agendaConfiguration.overwriteAvailability && isLocationAvailableByUser) || 
                              (agendaConfiguration.overwriteAvailability && isLocationAvailableByAgendaConfiguration))

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--mantine-background)',
      }}
    >
      <div className={classes.center}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '15px',
          }}
        >
          <Title className={classes.title}>{t('save_booking_page.pick_date.title')}</Title>
        </div>
        <Stepper
          active={active}
          onStepClick={() => {}}
          size="sm"
          styles={{
            steps: {
              padding: '0 15%',
            },
            stepBody: {
              display: 'none',
            },

            step: {
              padding: 0,
            },

            stepIcon: {
              borderWidth: rem(4),
            },

            separator: {
              marginLeft: rem(-2),
              marginRight: rem(-2),
              height: rem(10),
            },
          }}
        >
          {listAgendaConfs.length > 1 && <Stepper.Step label="Select booking type" description="Select booking type">
              <SaveBookingChooseType 
                listAgendaConfs={listAgendaConfs} 
                localUser={localUser} 
                selectedAgId={selectedAgId} 
                setSelectedAgId={(value: number) => setSelectedAgId(value)}
                nextStep={nextStep}
              />
          </Stepper.Step>}
          <Stepper.Step label="Select booking time" description="Select booking time">
            {agendaConfiguration && <SaveBookingDate 
              loading={loading}
              setStartDateToBooking={(date: Date) => {
                setCreateBooking(current => {
                  return {
                    ...current,
                    startDate: date
                  }
                })
              }}
              nextStep={nextStep}
              prevStep={prevStep}
              localUser={localUser}
              agendaConfiguration={agendaConfiguration}
              extension={extension}
              hasPrevStep={!!listAgendaConfs && listAgendaConfs?.length > 1}
            />}
          </Stepper.Step>
          <Stepper.Step
            label="Provide Contact Information"
            description="Provide Contact Information"
          >
            <div style={{ marginTop: '16px' }}>
              <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />
            </div>

            {agendaConfiguration && <SaveBookingContactInfo
              createBooking={createBooking}
              setCreateBooking={setCreateBooking}
              isLocationAvailable={isLocationAvailable}
              loading={loading}
              onClick={() => {
                if (agendaConfiguration.paymentOption === 2) {
                  doCreateBookingCheckout()
                } else {
                  doCreateBooking()
                }
              }}
              prevStep={prevStep}
              defaultCountryCode={localUser.defaultCountryCode}
            />}
          </Stepper.Step>
          {agendaConfiguration?.paymentOption === 2 && (
            <Stepper.Step label="Payment" description="Payment">
              {receivedBooking && (
                <Card
                  shadow="sm"
                  padding="0"
                  radius="md"
                  withBorder
                  style={{
                    margin: 'auto',
                    // height: '600px',
                    width: '90%',
                    position: 'relative',
                  }}
                >
                  {stripe ? (
                    <div id="checkout">
                      <EmbeddedCheckoutProvider stripe={stripe} options={stripeOptions}>
                        <EmbeddedCheckout />
                      </EmbeddedCheckoutProvider>
                    </div>
                  ) : (
                    <Center style={{ height: '100%' }}>
                      <Loader />
                    </Center>
                  )}
                </Card>
              )}
            </Stepper.Step>
          )}
          <Stepper.Step label="Done" description="Done">
            {agendaConfiguration && <SaveBookingEnd
              agendaConfiguration={agendaConfiguration}
              localUser={localUser}
              extension={extension}
              isOnSite={createBooking.onSite}
              receivedBooking={receivedBooking}
            />}
          </Stepper.Step>
          <Stepper.Completed>
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{
                margin: 'auto',
                height: '400px',
                width: '80%',
                position: 'relative',
              }}
            >
              Sesión cancelada exitosamente
            </Card>
          </Stepper.Completed>
        </Stepper>
      </div>
    </div>
  );
};
